import React from 'react';
import { motion } from 'motion/react';
import { Zap, Twitter, Github, Linkedin, Mail } from 'lucide-react';
import { useRouter } from '../App';

export function Footer() {
  const { navigate } = useRouter();

  const footerLinks = {
    Product: [
      { name: 'Product', page: 'product' },
      { name: 'Features', page: 'features' },
      { name: 'Pricing', page: 'pricing' },
      { name: 'Documentation', page: 'docs' },
      { name: 'API Reference', page: 'api-reference' },
    ],
    Company: [
      { name: 'About Us', page: 'about' },
      { name: 'Blog', page: 'blog' },
      { name: 'Careers', page: 'careers' },
      { name: 'Contact', page: 'contact' },
    ],
    Resources: [
      { name: 'Help Center', page: 'help-center' },
      { name: 'Status Page', page: 'status' },
      { name: 'Security', page: 'security' },
      { name: 'Privacy Policy', page: 'privacy' },
    ],
    Developers: [
      { name: 'API Docs', page: 'api-docs' },
      { name: 'SDKs', page: 'sdks' },
      { name: 'Integrations', page: 'integrations-dev' },
      { name: 'Webhooks', page: 'webhooks' },
    ],
  };

  const socialLinks = [
    { icon: Twitter, href: 'https://twitter.com/CoreMonit', name: 'Twitter' },
    { icon: Github, href: 'https://github.com/coremonit', name: 'GitHub' },
    { icon: Linkedin, href: 'https://linkedin.com/company/coremonit', name: 'LinkedIn' },
    { icon: Mail, href: 'mailto:support@coremonit.com', name: 'Email' },
  ];

  return (
    <footer className="relative bg-gradient-to-t from-muted/20 to-transparent border-t border-border/50">
      {/* Animated wave background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -bottom-10 left-0 right-0 h-20 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-cyan-500/5"
          animate={{
            backgroundPosition: ['0% 0%', '100% 0%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            backgroundSize: '200% 100%',
          }}
        />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Logo and Description */}
          <div className="lg:col-span-2">
            <motion.div
              className="flex items-center space-x-2 mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500 rounded-lg blur-md opacity-50 animate-pulse" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                CoreMonit
              </span>
            </motion.div>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              The future of monitoring: AI that sees, understands, and fixes. 
              Unified monitoring for servers, applications, microservices, databases, and cloud infrastructure.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors duration-300 group"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <social.icon className="w-4 h-4 text-muted-foreground group-hover:text-cyan-500 transition-colors duration-300" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="font-semibold mb-4 text-foreground">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <motion.button
                      type="button"
                      onClick={() => navigate(link.page)}
                      className="text-muted-foreground hover:text-cyan-500 transition-colors duration-300 text-sm"
                      whileHover={{ x: 4 }}
                    >
                      {link.name}
                    </motion.button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <motion.div
          className="mt-12 pt-8 border-t border-border/50 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm text-muted-foreground">
            © 2024 CoreMonit. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-4 justify-center sm:justify-end text-sm">
            <button
              type="button"
              className="text-muted-foreground hover:text-cyan-500 transition-colors duration-300"
              onClick={() => navigate('terms')}
            >
              Terms of Service
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-cyan-500 transition-colors duration-300"
              onClick={() => navigate('privacy')}
            >
              Privacy Policy
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-cyan-500 transition-colors duration-300"
              onClick={() => navigate('cookies')}
            >
              Cookie Policy
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-cyan-500 transition-colors duration-300"
              onClick={() => navigate('trust-center')}
            >
              Trust Center
            </button>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
